/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from "react";
import Section from "../../../components/common/Section";
import { IconPencil, IconPlus, IconMinus } from "@tabler/icons-react";
import ListView from "../../../components/common/ListView";
import {
  useFetchServicesData,
  useServicesCreateMutation,
  useUpdateService,
} from "../../../apis/servicesApis";
import formatDate from "../../../utilities/helper";
import { useForm } from "react-hook-form";
import Input from "../../../components/Input";
import toast from "react-hot-toast";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Pagination from "../../../components/common/Pagination";
import SelectInput from "../../../components/Select";
import { useLanguage } from "../../../context/LanguageContext";

type FormValues = {
  name: string;
};

export default function Service() {
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [mode, setMode] = useState<"add" | "edit" | null>(null);
  const [serviceId, setServiceId] = useState<number | null>(null);
  const { t } = useLanguage();

  const {
    data: servicesData,
    isLoading: servicesLoading,
    isFetching,
    refetch: servicesRefetch,
  } = useFetchServicesData(pageNo, limit, "");

  const { mutate, isPending } = useServicesCreateMutation();
  const { mutate: updateService, isPending: ServicePending } =
    useUpdateService();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>();

  const clearState = () => {
    reset();
    setServiceId(null);
    setMode(null);
  };

  const handleAddClick = () => {
    if (mode === "add" || mode === "edit") {
      clearState();
    } else {
      reset();
      setServiceId(null);
      setMode("add");
    }
  };

  const handleEditClick = (item: { id: number; serviceName: string }) => {
    reset();
    setValue("name", item?.serviceName);
    setServiceId(item?.id);
    setMode("edit");

    setTimeout(() => {
      // Primary: Try common dashboard content containers
      const selectors = [
        ".content",
        ".main-content",
        ".dashboard-content",
        "main",
        "[role='main']",
        ".overflow-y-auto",
        ".scrollable-content",
      ];

      let scrolled = false;
      for (const selector of selectors) {
        const el = document.querySelector(selector) as HTMLElement;
        if (el && el.scrollHeight > el.clientHeight) {
          el.scrollTo({ top: 0, behavior: "smooth" });
          scrolled = true;
          break;
        }
      }

      // Fallback: window
      if (!scrolled) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 100);
  };
  const handleAddService = (data: FormValues) => {
    const trimmedName = data.name.trim();
    mutate(
      { name: trimmedName },
      {
        onSuccess: () => {
          toast.success(t("Service added successfully ✅"));
          servicesRefetch();
          clearState();
        },
        onError: (error) => {
          toast.error(String(error));
        },
      }
    );
  };

  const handleUpdateService = (data: FormValues) => {
    if (!serviceId) return;
    const trimmedName = data.name.trim();

    updateService(
      { id: serviceId, name: trimmedName },
      {
        onSuccess: () => {
          toast.success(t("Service updated successfully ✅"));
          servicesRefetch();
          clearState();
        },
        onError: (error) => {
          toast.error(String(error));
        },
      }
    );
  };

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  return (
    <div className="space-y-4 border border-[#d2d2d2] rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] mb-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-lg font-medium text-[#212121]">{t("Services Info")}</p>
        <button
          onClick={handleAddClick}
          className="bg-[#FBD037] text-[#212121] text-sm font-semibold p-2 rounded-full"
        >
          {mode ? (
            <IconMinus stroke={1.5} size={20} />
          ) : (
            <IconPlus stroke={1.5} size={20} />
          )}
        </button>
      </div>

      {/* Add/Edit Form */}
      {(mode === "add" || mode === "edit") && (
        <div className="bg-[#FFFFFF] p-4 rounded-lg border border-[#D2D2D2]">
          <Section
            className="text-base space-y-4"
            title={mode === "add" ? t("Add Service") : t("Edit Service")}
          >
            <SelectInput
              name="name"
              className="col-span-3"
              label={t("Service Name")}
              placeholder={t("Select a Service option")}
              selectOptions={[
                {
                  id: "standard",
                  value: "Standard Delivery",
                  text: t("Standard Delivery — Delivery within 1–5 days based on location."),
                },
                {
                  id: "express",
                  value: "Express / Priority Delivery",
                  text: t("Express / Priority Delivery — Faster delivery within 1–2 days."),
                },
                {
                  id: "sameday",
                  value: "Same-Day Delivery",
                  text: t("Same-Day Delivery — Order delivered within 24 hours."),
                },
                {
                  id: "scheduled",
                  value: "Scheduled Delivery",
                  text: t("Scheduled Delivery — Delivered at a chosen date and time slot."),
                },
                {
                  id: "bullet",
                  value: "Bullet Delivery",
                  text: t("Bullet Delivery — Ultra-fast delivery within 3–6 hours."),
                },
              ]}
              register={register}
              validation={{
                required: t("Service Name is required"),
              }}
              value={watch("name")}
              error={errors.name}
              setValue={(val: string) => setValue("name", val)}
            />
          </Section>

          <div className="mt-4 flex gap-2">
            <button
              onClick={handleSubmit((data) => {
                if (mode === "add") {
                  handleAddService(data);
                } else if (mode === "edit") {
                  handleUpdateService(data);
                }
              })}
              disabled={isPending || ServicePending}
              className="bg-[#FBD037] text-[#212121] text-sm font-semibold px-4 py-2 rounded-full disabled:opacity-50"
            >
              {mode === "add"
                ? isPending
                  ? t("Saving...")
                  : t("Save")
                : ServicePending
                ? t("Updating...")
                : t("Update")}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="-mx-4">
        {servicesLoading || isFetching ? (
          <TableSkeleton
            mainClass="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5]"
            subClassName=""
            columns={[
              "#",
              t("Service Name"),
              t("Created By"),
              t("Date"),
              t("Actions"),
            ]}
            rows={6}
          />
        ) : servicesData?.results?.length ? (
          <ListView
            tdClassName="rounded-r-none rounded-l-none"
            mainDivClassName="bg-transparent"
            trClassName="hover:bg-[#FBD037]/20"
            mailDivClassName="p-0 bg-transparent "
            thClassName="border-t"
            data={servicesData?.results}
            onRowClick={(client) => console.log("Clicked:", client)}
            columns={[
              {
                label: "#",
                render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
              },
              { label: t("Service Name"), render: (c: any) => t(c.name) },
              { label: t("Created By"), render: (c: any) => c.createdByName },
              {
                label: t("Date"),
                render: (c: any) => formatDate(c.created_at),
              },
              {
                label: t("Actions"),
                render: (c: any) => (
                  <div className="flex justify-center items-center">
                    <button
                      onClick={() =>
                        handleEditClick({ id: c.id, serviceName: c.name })
                      }
                      className="p-2 border border-[#D2D2D2] rounded-full"
                    >
                      <IconPencil size={20} />
                    </button>
                  </div>
                ),
              },
            ]}
          />
        ) : (
          <NoDataFound
            subClassName=""
            mainClassName="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] rounded-b-3xl"
          />
        )}

        {servicesData?.totalPages > 1 && (
          <div className="flex justify-center items-center py-4">
            <Pagination
              currentPage={pageNo}
              totalPages={servicesData?.totalPages}
              fetchData={handlePaginationFetch}
            />
          </div>
        )}
      </div>
    </div>
  );
}
